import { useQuery } from '@tanstack/react-query';
import apiClient from '../clients/apiClient';
import type { Application } from '../data/mock';

interface OutreachItem {
  id: string;
  recruiter_name: string;
  title: string;
  status: string;
  sent_at: string | null;
  gmail_thread_id: string | null;
}

interface ApplicationResponse {
  id: string;
  role: string | null;
  company: string | null;
  domain: string | null;
  outreach_status: string | null;
  created_at: string | null;
  sent_at: string | null;
  outreach_count: number;
  sent_count: number;
  replied_count: number;
  bounced_count: number;
  outreaches: OutreachItem[];
}

function mapStatus(s: string | null): Application['status'] {
  if (s === 'finding_recruiters') return 'finding_recruiters';
  if (s === 'drafts_ready') return 'drafts_ready';
  if (s === 'completed') return 'completed';
  return 'in_progress';
}

function mapApplicationResponse(a: ApplicationResponse): Application {
  const outreaches = (a.outreaches ?? []).map(o => ({
    id: o.id,
    recruiter_name: o.recruiter_name,
    title: o.title,
    status: o.status as import('../data/mock').Outreach['status'],
    sent_at: o.sent_at,
    gmail_thread_id: o.gmail_thread_id,
  }));
  return {
    id: a.id,
    company: a.company ?? '',
    role: a.role ?? '',
    domain: a.domain ?? '',
    date: a.created_at ? new Date(a.created_at).toLocaleDateString() : '',
    status: mapStatus(a.outreach_status),
    outreach_count: outreaches.length || a.outreach_count,
    sent_count: outreaches.filter(o => o.status === 'sent' || o.status === 'replied' || o.status === 'bounced').length,
    replied_count: a.replied_count,
    draft_count: outreaches.filter(o => o.status === 'draft_ready').length,
    outreaches,
  };
}

async function fetchPipeline(): Promise<Application[]> {
  const { data } = await apiClient.get<ApplicationResponse[]>('/api/pipeline');
  return data.map(mapApplicationResponse);
}

export function usePipeline() {
  return useQuery<Application[]>({
    queryKey: ['pipeline'],
    queryFn: fetchPipeline,
    refetchInterval: 30_000,
  });
}

async function fetchApplication(id: string): Promise<Application> {
  const { data } = await apiClient.get<ApplicationResponse>(`/api/pipeline/${id}`);
  return mapApplicationResponse(data);
}

export function useApplication(id: string) {
  return useQuery<Application>({
    queryKey: ['application', id],
    queryFn: () => fetchApplication(id),
    enabled: !!id,
  });
}
